import { primitiveColorTokens, tokens } from '@cellula/react-theme-patient';
import * as RadixAvatar from '@radix-ui/react-avatar';
import { createInitials } from '@visiba/std';
import { Fragment, ReactNode } from 'react';
import styled, { css } from 'styled-components';

type Size = 'small' | 'medium' | 'large';

interface PropsWithImage {
	src?: string | null;
	name: string;
	size?: Size;
	icon?: ReactNode;
}

interface PropsWithIcon {
	src?: never;
	name?: never | null;
	size?: Size;
	icon: ReactNode;
}

type Props = PropsWithImage | PropsWithIcon;

const elements = {
	root: styled(RadixAvatar.Root)<{ size: Size }>`
		display: inline-flex;
		align-items: center;
		justify-content: center;
		vertical-align: middle;
		overflow: hidden;
		user-select: none;
		border-radius: 100%;
		background-color: ${primitiveColorTokens.neutral[100]};
		flex-shrink: 0;

		${({ size }) => {
			switch (size) {
				case 'small':
					return css`
						--_avatar-size: 32px;
					`;
				case 'medium':
					return css`
						--_avatar-size: 40px;
					`;
				case 'large':
					return css`
						--_avatar-size: 48px;
					`;
				default:
					return null;
			}
		}}

		width: var(--_avatar-size);
		height: var(--_avatar-size);
	`,
	image: styled(RadixAvatar.Image)`
		width: 100%;
		height: 100%;
		object-fit: cover;
		border-radius: inherit;
	`,
	icon: styled.div`
		width: 100%;
		height: 100%;
		object-fit: cover;
		border-radius: inherit;
		display: flex;
		justify-content: center;
		align-items: center;
		background-color: ${tokens.color.bg.surfaceBrand};
		color: ${tokens.color.content.mutedBrand};
		font-size: var(--_avatar-size);
	`,
	fallback: styled(RadixAvatar.Fallback)`
		width: 100%;
		height: 100%;
		display: flex;
		align-items: center;
		justify-content: center;
		background-color: ${tokens.color.bg.surfaceBrand};
		color: ${tokens.color.content.mutedBrand};
		font: ${tokens.text.label.largeSemiBold};
	`,
};

export function Avatar({ src, name, size = 'medium', icon }: Props): JSX.Element {
	return (
		<elements.root size={size}>
			<IllustrativeContent src={src} fallback={name} icon={icon} />
		</elements.root>
	);
}

interface IllustrativePropsWithSrc {
	src: string;
	fallback: string;
	icon?: never;
}

interface IllustrativePropsWithIcon {
	src?: string | null;
	fallback?: string | null;
	icon: ReactNode;
}

type IllustrativeProps = IllustrativePropsWithSrc | IllustrativePropsWithIcon;

function IllustrativeContent({ fallback, src, icon }: IllustrativeProps): JSX.Element {
	if (fallback != null) {
		const initials = createInitials(fallback ?? '', '');

		return (
			<Fragment>
				{src != null ? <elements.image aria-hidden='true' src={src} /> : null}
				<elements.fallback aria-hidden='true' delayMs={src != null ? 600 : undefined}>
					{initials}
				</elements.fallback>
			</Fragment>
		);
	}

	return <elements.icon aria-hidden='true'>{icon}</elements.icon>;
}
